import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReportWidgetTableLoadMoreProps {
    loadMoreVariant: 'button' | 'link';
    loadingMore: boolean;
    onLoadMore?: () => void;
    onLoadMoreHover?: (hovering: boolean) => void;
    onCollapse?: () => void;
    loadMoreCount: number;
    modelLabel?: string;
    isFullyExpanded?: boolean;
}

function resolveLoadMoreLabel(params: {
    loadMoreVariant: 'button' | 'link';
    modelLabel?: string;
    loadMoreCount: number;
    t: (key: string, options?: Record<string, unknown>) => string;
}): string {
    const { loadMoreVariant, modelLabel, loadMoreCount, t } = params;

    if (modelLabel) {
        return t('composableReportWidget.showMoreModelLink', {
            count: loadMoreCount,
            model: modelLabel,
        });
    }

    if (loadMoreVariant === 'link') {
        return t('composableReportWidget.showMoreLink', {
            count: loadMoreCount,
        });
    }

    return t('composableReportWidget.loadMore');
}

export const ReportWidgetTableLoadMore = ({
    loadMoreVariant,
    loadingMore,
    onLoadMore,
    onLoadMoreHover,
    loadMoreCount,
    modelLabel,
    isFullyExpanded = false,
    onCollapse,
}: ReportWidgetTableLoadMoreProps): JSX.Element => {
    const { t } = useTranslation();

    const showCollapse = isFullyExpanded && !loadingMore && Boolean(onCollapse);

    const resolvedLoadMoreLabel = resolveLoadMoreLabel({
        loadMoreVariant,
        modelLabel,
        loadMoreCount,
        t,
    });

    const collapseLabel = modelLabel
        ? t('composableReportWidget.collapseModel', {
              model: modelLabel,
          })
        : t('composableReportWidget.collapse');

    const clickAction = showCollapse ? onCollapse : onLoadMore;
    const clickHandler = loadingMore ? undefined : clickAction;

    const labelText = showCollapse
        ? collapseLabel
        : resolvedLoadMoreLabel.replace('{{count}}', String(loadMoreCount));

    const moreIcon = showCollapse ? (
        <ChevronUpIcon sx={{ fontSize: '1.8rem' }} />
    ) : (
        <ChevronDownIcon sx={{ fontSize: '1.8rem' }} />
    );

    const linkLeading = loadingMore ? (
        <CircularProgress size={16} sx={{ marginRight: 0.5 }} />
    ) : (
        moreIcon
    );

    const buttonStartIcon = loadingMore ? (
        <CircularProgress size={16} />
    ) : undefined;

    const outerSx = {
        paddingTop: 2,
        paddingBottom: 1,
        paddingLeft: 0,
        paddingRight: 0,
        display: 'flex',
        justifyContent: 'flex-start',
    } as const;

    const innerSx = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 32,
    } as const;

    if (loadMoreVariant === 'link') {
        return (
            <Box sx={outerSx}>
                <Box sx={innerSx}>
                    <Typography
                        component="button"
                        variant="body1"
                        onClick={clickHandler}
                        data-testclass="composable-report-widget-load-more"
                        onMouseEnter={() => onLoadMoreHover?.(true)}
                        onMouseLeave={() => onLoadMoreHover?.(false)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: loadingMore ? 'default' : 'pointer',
                            border: 'none',
                            background: 'none',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            padding: 0,
                            opacity: loadingMore ? 0.7 : 1,
                            '&:hover': {
                                color: loadingMore
                                    ? 'text.secondary'
                                    : 'text.primary',
                            },
                        }}
                    >
                        {linkLeading}
                        {labelText}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={outerSx}>
            <Box sx={innerSx}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={clickHandler}
                    disabled={loadingMore}
                    data-testclass="composable-report-widget-load-more"
                    startIcon={buttonStartIcon}
                >
                    {labelText}
                </Button>
            </Box>
        </Box>
    );
};
